import { useUser } from '@clerk/clerk-react';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';
import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	Outlet,
	useRouter,
} from '@tanstack/react-router';
import { useAuth } from '@uturn/portal/modules/auth/hooks';
import type { Auth } from '@uturn/portal/modules/auth/provider';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorFallback } from '../components/error-fallback';
import { DevTools } from '../dev';
import { logErrorToSentry } from '../lib/log-error-to-sentry';
import { NotFound } from '../pages/not-found';

type RouterContext = {
	auth: Auth;
	queryClient: QueryClient;
};

const RootComponent = () => {
	const { user } = useUser();
	
	useEffect(() => {
		if (!user) {
			return;
		}

		Sentry.setUser({
			id: user.id,
			email: user.primaryEmailAddress?.emailAddress,
			fullName: user.fullName ?? undefined,
		});
	}, [user]);

	return (
		<>
			<Outlet />
			{import.meta.env.DEV && <DevTools />}
		</>
	);
};

const RootErrorComponent = ({ error }: ErrorComponentProps) => {
	const { t } = useTranslation();
	const { user } = useUser();
	const { logout } = useAuth();
	const router = useRouter();

	// Log to Sentry with XID. Reporting the same error twice returns the same XID
	// and captures only once, so calling this on re-render is safe.
	const xid = logErrorToSentry({
		error,
		context: 'RootRouteError',
		level: 'error',
	});

	const handleLogout = async () => {
		await logout();
		router.navigate({ to: '/sign-in' });
	};

	return (
		<ErrorFallback
			xid={xid}
			icon={faTriangleExclamation}
			title={t('errors.generic.title', 'Something went wrong')}
			message={t(
				'errors.generic.message',
				"We've encountered an unexpected error. Our team has been notified and will investigate the issue.",
			)}
			errorReferenceLabel={t('errors.error-reference', 'Error Reference')}
			errorReferenceDescription={t(
				'errors.provide-code-when-reporting',
				'Please provide this code when reporting the issue',
			)}
			onLogout={handleLogout}
			user={
				user
					? {
							name: user.fullName ?? 'User',
							role: (user.publicMetadata?.role as string) ?? '',
						}
					: null
			}
		/>
	);
};

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootComponent,
	errorComponent: RootErrorComponent,
	notFoundComponent: NotFound,
});
